import { fetchDataApi } from '../../../shared/api/fetchDataApi';
import type {
  CommitExistingDatasetImportPayload,
  CommitNewDatasetImportPayload,
  CreateDatasetPayload,
  CreateMergeDatasetPayload,
  DatasetDetail,
  DatasetImportCommitResult,
  DatasetImportPreview,
  DatasetMediaLink,
  DatasetRow,
  DatasetRowsPage,
  DatasetSummary,
  FileLinkedRowsResult,
  ProfileDatasetsResponse,
  ReferenceKeysQuery,
  ReferenceKeysResult,
  RecordOptionsQuery,
  RecordOptionsResult,
  RowLinkedFilesResult,
  UpsertDatasetMediaLinkPayload,
  UpdateDatasetPayload,
  UpdateDatasetSchemaPayload,
  UpdateMergeDatasetColumnsPayload,
  WorkspaceDatasetsResponse,
  WorkspaceDatasetGraphResponse,
} from '../types/datasetTypes';
import { isListSourceDataset } from '../utils/schema/listSourceDatasets';
import { isTableSourceDataset } from '../utils/schema/tableSourceDatasets';

export type {
  DatasetSummary,
  ProfileDatasetSection,
  ProfileDatasetsResponse,
  DatasetDetail,
  DatasetColumn,
  DatasetColumnType,
  DatasetRow,
  CreateDatasetPayload,
  CreateMergeDatasetPayload,
  UpdateMergeDatasetColumnsPayload,
  UpdateDatasetPayload,
  UpdateDatasetSchemaPayload,
  DraftColumn,
  ReferenceKeysResult,
  ReferenceKeysQuery,
  RecordCellDisplay,
  RecordOption,
  RecordOptionsQuery,
  RecordOptionsResult,
  DatasetRecordColumnMeta,
  WorkspaceDatasetsResponse,
  WorkspaceDatasetGraphResponse,
  DatasetGraphNode,
  DatasetGraphEdge,
  DatasetGraphEdgeType,
} from '../types/datasetTypes';

type ApiSuccess<T> = T & { success: boolean };

export async function apiListDatasetsForProfile(): Promise<ProfileDatasetsResponse> {
  const data = await fetchDataApi<ApiSuccess<ProfileDatasetsResponse>>('/datasets/profile', {
    method: 'GET',
  });

  return {
    own: data.own ?? [],
    shared: data.shared ?? [],
  };
}

export async function apiListDatasetsByRoot(
  rootFolderId: string,
): Promise<WorkspaceDatasetsResponse> {
  const data = await fetchDataApi<ApiSuccess<WorkspaceDatasetsResponse>>(
    `/datasets?rootFolderId=${encodeURIComponent(rootFolderId)}`,
    { method: 'GET' },
  );

  return {
    rootFolderId: data.rootFolderId ?? rootFolderId,
    accessRole: data.accessRole,
    canEditSchema: data.canEditSchema,
    canEditRows: data.canEditRows,
    canDelete: data.canDelete,
    canExport: data.canExport,
    datasets: data.datasets ?? [],
  };
}

export async function apiGetDatasetGraph(
  rootFolderId: string,
): Promise<WorkspaceDatasetGraphResponse> {
  const data = await fetchDataApi<ApiSuccess<WorkspaceDatasetGraphResponse>>(
    `/datasets/graph?rootFolderId=${encodeURIComponent(rootFolderId)}`,
    { method: 'GET' },
  );

  return {
    rootFolderId: data.rootFolderId ?? rootFolderId,
    accessRole: data.accessRole,
    canEditSchema: data.canEditSchema,
    canEditRows: data.canEditRows,
    canDelete: data.canDelete,
    canExport: data.canExport,
    nodes: data.nodes ?? [],
    edges: data.edges ?? [],
  };
}

export async function apiListReferenceLists(
  rootFolderId: string,
): Promise<DatasetSummary[]> {
  const { datasets } = await apiListDatasetsByRoot(rootFolderId);
  return datasets.filter(isListSourceDataset);
}

export async function apiListTableDatasets(
  rootFolderId: string,
): Promise<DatasetSummary[]> {
  const { datasets } = await apiListDatasetsByRoot(rootFolderId);
  return datasets.filter(isTableSourceDataset);
}

export async function apiGetDataset(datasetId: string): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>(`/datasets/${datasetId}`, {
    method: 'GET',
  });
  return data.dataset;
}

export async function apiCreateDataset(payload: CreateDatasetPayload): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>('/datasets', {
    method: 'POST',
    data: payload,
  });
  return data.dataset;
}

export async function apiCreateMergeDataset(
  payload: CreateMergeDatasetPayload,
): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>('/datasets/merge', {
    method: 'POST',
    data: payload,
  });
  return data.dataset;
}

export async function apiUpdateMergeDatasetColumns(
  datasetId: string,
  payload: UpdateMergeDatasetColumnsPayload,
): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>(
    `/datasets/${datasetId}/merge-columns`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return data.dataset;
}

export async function apiUpdateDataset(
  datasetId: string,
  payload: UpdateDatasetPayload,
): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>(
    `/datasets/${datasetId}`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return data.dataset;
}

export async function apiUpdateDatasetSchema(
  datasetId: string,
  payload: UpdateDatasetSchemaPayload,
): Promise<DatasetDetail> {
  const data = await fetchDataApi<ApiSuccess<{ dataset: DatasetDetail }>>(
    `/datasets/${datasetId}/schema`,
    {
      method: 'PATCH',
      data: payload,
    },
  );
  return data.dataset;
}

export async function apiListDatasetRows(
  datasetId: string,
  query: {
    page?: number;
    limit?: number;
    sortColumnId?: string;
    sortDir?: 'asc' | 'desc';
    filterColumnId?: string;
    filterOp?: 'contains' | 'empty' | 'not_empty';
    filterValue?: string;
    forExport?: boolean;
  } = {},
): Promise<DatasetRowsPage> {
  const params = new URLSearchParams();
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.sortColumnId) params.set('sortColumnId', query.sortColumnId);
  if (query.sortDir) params.set('sortDir', query.sortDir);
  if (query.filterColumnId) params.set('filterColumnId', query.filterColumnId);
  if (query.filterOp) params.set('filterOp', query.filterOp);
  if (query.filterValue) params.set('filterValue', query.filterValue);
  if (query.forExport) params.set('forExport', '1');
  const suffix = params.toString() ? `?${params.toString()}` : '';

  const data = await fetchDataApi<ApiSuccess<DatasetRowsPage>>(
    `/datasets/${datasetId}/rows${suffix}`,
    { method: 'GET' },
  );

  return {
    items: data.items ?? [],
    pagination: data.pagination,
  };
}

export async function apiCreateDatasetRow(
  datasetId: string,
  cells: Record<string, unknown>,
): Promise<DatasetRow> {
  const data = await fetchDataApi<ApiSuccess<{ row: DatasetRow }>>(`/datasets/${datasetId}/rows`, {
    method: 'POST',
    data: { cells },
  });
  return data.row;
}

export async function apiUpdateDatasetRow(
  datasetId: string,
  rowId: string,
  cells: Record<string, unknown>,
): Promise<DatasetRow> {
  const data = await fetchDataApi<ApiSuccess<{ row: DatasetRow }>>(
    `/datasets/${datasetId}/rows/${rowId}`,
    {
      method: 'PATCH',
      data: { cells },
    },
  );
  return data.row;
}

export async function apiGetDatasetRow(
  datasetId: string,
  rowId: string,
): Promise<DatasetRow> {
  const data = await fetchDataApi<ApiSuccess<{ row: DatasetRow }>>(
    `/datasets/${datasetId}/rows/${rowId}`,
    { method: 'GET' },
  );
  return data.row;
}

export async function apiDeleteDatasetRow(
  datasetId: string,
  rowId: string,
): Promise<void> {
  await fetchDataApi(`/datasets/${datasetId}/rows/${rowId}`, {
    method: 'DELETE',
  });
}

export type DeleteDatasetResult = {
  deletedMergeDatasets?: Array<{ id: string; name: string }>;
};

export async function apiDeleteDataset(datasetId: string): Promise<DeleteDatasetResult> {
  const data = await fetchDataApi<ApiSuccess<DeleteDatasetResult>>(`/datasets/${datasetId}`, {
    method: 'DELETE',
  });
  return data;
}

export async function apiPreviewNewDatasetImport(file: File): Promise<DatasetImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('originalFileName', file.name);

  const data = await fetchDataApi<ApiSuccess<{ preview: DatasetImportPreview }>>(
    '/datasets/import/preview',
    {
      method: 'POST',
      data: formData,
    },
  );

  return data.preview;
}

export async function apiCommitNewDatasetImport(
  payload: CommitNewDatasetImportPayload,
): Promise<DatasetImportCommitResult> {
  const data = await fetchDataApi<ApiSuccess<DatasetImportCommitResult>>(
    '/datasets/import/commit',
    {
      method: 'POST',
      data: payload,
    },
  );

  return {
    dataset: data.dataset,
    importedCount: data.importedCount,
    updatedCount: data.updatedCount,
    mode: data.mode,
    fileActionSummary: data.fileActionSummary,
  };
}

export async function apiPreviewExistingDatasetImport(
  datasetId: string,
  file: File,
): Promise<DatasetImportPreview> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('originalFileName', file.name);

  const data = await fetchDataApi<ApiSuccess<{ preview: DatasetImportPreview }>>(
    `/datasets/${datasetId}/import/preview`,
    {
      method: 'POST',
      data: formData,
    },
  );

  return data.preview;
}

export async function apiCommitExistingDatasetImport(
  datasetId: string,
  payload: CommitExistingDatasetImportPayload,
): Promise<DatasetImportCommitResult> {
  const data = await fetchDataApi<ApiSuccess<DatasetImportCommitResult>>(
    `/datasets/${datasetId}/import/commit`,
    {
      method: 'POST',
      data: payload,
    },
  );

  return {
    dataset: data.dataset,
    importedCount: data.importedCount,
    updatedCount: data.updatedCount,
    mode: data.mode,
    fileActionSummary: data.fileActionSummary,
  };
}

export async function apiGetDatasetMediaLink(
  datasetId: string,
): Promise<DatasetMediaLink | null> {
  const data = await fetchDataApi<ApiSuccess<{ link: DatasetMediaLink | null }>>(
    `/datasets/${datasetId}/link`,
    { method: 'GET' },
  );

  return data.link ?? null;
}

export async function apiUpsertDatasetMediaLink(
  datasetId: string,
  payload: UpsertDatasetMediaLinkPayload,
): Promise<DatasetMediaLink> {
  const data = await fetchDataApi<ApiSuccess<{ link: DatasetMediaLink }>>(
    `/datasets/${datasetId}/link`,
    {
      method: 'PUT',
      data: payload,
    },
  );

  return data.link;
}

export async function apiDeleteDatasetMediaLink(datasetId: string): Promise<void> {
  await fetchDataApi(`/datasets/${datasetId}/link`, {
    method: 'DELETE',
  });
}

export async function apiGetRowLinkedFiles(
  datasetId: string,
  rowId: string,
  options: { forExport?: boolean } = {},
): Promise<RowLinkedFilesResult> {
  const params = new URLSearchParams();
  if (options.forExport) {
    params.set('forExport', '1');
  }
  const query = params.toString();
  const data = await fetchDataApi<ApiSuccess<RowLinkedFilesResult>>(
    `/datasets/${datasetId}/rows/${rowId}/files${query ? `?${query}` : ''}`,
    { method: 'GET' },
  );

  return {
    files: data.files ?? [],
    total: data.total ?? 0,
    hasMore: data.hasMore ?? false,
    matchedTag: data.matchedTag ?? null,
  };
}

export async function apiGetReferenceKeys(
  listId: string,
  query: ReferenceKeysQuery = {},
): Promise<ReferenceKeysResult> {
  const params = new URLSearchParams();
  if (query.q?.trim()) {
    params.set('q', query.q.trim());
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.cursor?.trim()) {
    params.set('cursor', query.cursor.trim());
  }

  const suffix = params.toString() ? `?${params.toString()}` : '';
  const data = await fetchDataApi<ApiSuccess<ReferenceKeysResult>>(
    `/datasets/${listId}/reference-keys${suffix}`,
    { method: 'GET' },
  );

  return {
    keys: data.keys ?? [],
    hasMore: data.hasMore ?? false,
    nextCursor: data.nextCursor,
  };
}

export async function apiGetRecordOptions(
  referencedDatasetId: string,
  query: RecordOptionsQuery,
): Promise<RecordOptionsResult> {
  const params = new URLSearchParams();
  params.set('displayColumnId', query.displayColumnId.trim());

  if (query.q?.trim()) {
    params.set('q', query.q.trim());
  }
  if (query.limit !== undefined) {
    params.set('limit', String(query.limit));
  }
  if (query.cursor?.trim()) {
    params.set('cursor', query.cursor.trim());
  }

  const data = await fetchDataApi<ApiSuccess<RecordOptionsResult>>(
    `/datasets/${referencedDatasetId}/record-options?${params.toString()}`,
    { method: 'GET' },
  );

  return {
    items: data.items ?? [],
    hasMore: data.hasMore ?? false,
    nextCursor: data.nextCursor,
  };
}

export async function apiGetFileLinkedRows(
  fileId: string,
  rootFolderId: string,
): Promise<FileLinkedRowsResult> {
  const data = await fetchDataApi<ApiSuccess<FileLinkedRowsResult>>(
    `/files/${fileId}/linked-rows?rootFolderId=${encodeURIComponent(rootFolderId)}`,
    { method: 'GET' },
  );

  return {
    groups: (data.groups ?? []).map((group) => ({
      ...group,
      displayColumns: group.displayColumns ?? [],
      link: group.link
        ? { ...group.link, displayColumnIds: group.link.displayColumnIds ?? [] }
        : group.link,
    })),
  };
}
